import TextAlign from '@tiptap/extension-text-align';
import StarterKit from '@tiptap/starter-kit';
import MenuBar from '../../BurnoutMenuBar';

import { EditorContent, useEditor } from '@tiptap/react';
import { FieldErrors } from './FieldErrors';

import { useEffect } from 'react';
import { useFieldContext } from '.';

interface Props {
  autoFocus?: boolean;
  content?: string;
  label: string;
  noLabel?: boolean;
  placeholder?: string;
  required?: boolean;
  setContent?: (content: string) => void;
  subLabel?: string;
}

export function BurnoutRichTextEditor({
  autoFocus = false,
  content,
  label,
  noLabel,
  placeholder,
  required,
  setContent,
  subLabel
}: Props) {
  const field = useFieldContext<string>();

  const editor = useEditor({
    immediatelyRender: false,
    onUpdate: ({ editor }) => (setContent ? setContent(editor.getHTML()) : field.handleChange(editor.getHTML())),
    extensions: [StarterKit, TextAlign.configure({ types: ['heading', 'paragraph'] })],
    content: content ? content : field.state.value,
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        class:
          'ring-burnout-light-red focus:ring-burnout-blue text-black nice-focus transition-all block w-full rounded-lg border-0 bg-white px-3 py-2 ring-2 duration-200 ease-in-out outline-none ring-inset placeholder:text-gray-400 focus:ring-inset min-h-[156px]'
      }
    }
  });

  // Update editor content when content prop changes
  useEffect(() => {
    const properContent = content ? content : field.state.value;

    if (editor && properContent !== editor.getHTML()) {
      editor.commands.setContent(properContent);
    }
  }, [content, editor, field.state.value]);

  return (
    <div className='w-full'>
      <label htmlFor={field.name} className={`${noLabel ? 'sr-only' : ''} block leading-6 font-medium`}>
        {label}
      </label>
      {subLabel && !noLabel && (
        <div className='mt-2'>
          <p className='text-sm text-black/75'>{subLabel}</p>
        </div>
      )}
      <div className={`${noLabel ? '' : 'mt-2'} flex flex-col gap-3`}>
        <MenuBar editor={editor} />
        <EditorContent
          name={field.name}
          id={field.name}
          required={required}
          editor={editor}
          placeholder={placeholder}
        />
      </div>
      <FieldErrors meta={field.state.meta} />
    </div>
  );
}
